import React, { useState, useEffect } from 'react';
import api from '../services/api';

const emptyForm = { username: '', fullName: '', email: '', companyId: '', role: 'DEVELOPER', password: '', active: true, apps: '' };
const ROLES = ['ADMIN', 'DEVELOPER', 'VIEWER'];
const APP_OPTIONS = [
  { key: 'devops', label: 'DevOps Center' },
  { key: 'pm', label: 'PM Center' },
  { key: 'support', label: 'Support Center' },
  { key: 'products', label: 'CLTS Products' },
];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { load(); loadCompanies(); }, []);

  const load = async () => {
    try { const r = await api.get('/users'); setUsers(r.data); }
    catch { setError('Failed to load users'); }
  };

  const loadCompanies = async () => {
    try { const r = await api.get('/companies'); setCompanies(r.data); } catch {}
  };

  const getCompanyName = (id) => {
    const c = companies.find(c => c.id === id);
    return c ? c.name : '—';
  };

  const openNew = () => { setForm(emptyForm); setEditing(null); setShowForm(true); setError(''); };

  const hasApp = (key) => (form.apps || '').split(',').map(s => s.trim()).includes(key);
  const toggleApp = (key) => {
    const set = new Set((form.apps || '').split(',').map(s => s.trim()).filter(Boolean));
    if (set.has(key)) set.delete(key); else set.add(key);
    setForm({ ...form, apps: Array.from(set).join(',') });
  };

  const openEdit = (u) => {
    setForm({ username: u.username, fullName: u.fullName, email: u.email || '', companyId: u.companyId || '', role: u.role, password: '', active: u.active, apps: u.apps || '' });
    setEditing(u);
    setShowForm(true);
    setError('');
  };

  const save = async () => {
    if (!form.username || !form.fullName) { setError('Username and Full Name are required'); return; }
    if (!editing && !form.password) { setError('Password is required for new users'); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          fullName: form.fullName, email: form.email, role: form.role, companyId: form.companyId ? String(form.companyId) : '',
          active: String(form.active), apps: form.apps, ...(form.password ? { password: form.password } : {})
        });
      } else {
        await api.post('/users', form);
      }
      setShowForm(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { active: String(!u.active) });
    load();
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"?`)) return;
    await api.delete(`/users/${u.id}`);
    load();
  };

  const doResetPassword = async () => {
    if (!resetPassword) return;
    await api.post(`/users/${resetUserId}/reset-password`, { password: resetPassword });
    setResetUserId(null); setResetPassword('');
    alert('Password reset successfully');
  };

  const roleColor = (role) => {
    if (role === 'ADMIN') return { background: '#fee2e2', color: '#b91c1c' };
    if (role === 'DEVELOPER') return { background: '#dbeafe', color: '#1d4ed8' };
    return { background: '#f3f4f6', color: '#374151' };
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: 22 }}>User Management</h2>
          <p style={{ color: '#64748b', fontSize: 14, marginTop: 2 }}>{users.length} user{users.length !== 1 ? 's' : ''}</p>
        </div>
        {currentUser.role === 'ADMIN' && (
          <button className="btn-primary" onClick={openNew}>+ New User</button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ fontWeight: 600, marginBottom: 16 }}>{editing ? 'Edit User' : 'New User'}</h3>
          {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Username *</label>
              <input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
                disabled={!!editing} placeholder="e.g. john.doe"
                style={{ width: '100%', opacity: editing ? 0.6 : 1 }} />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Full Name *</label>
              <input value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })}
                placeholder="e.g. John Doe" style={{ width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Email</label>
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="e.g. john@company.com" style={{ width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Company</label>
              <select value={form.companyId} onChange={e => setForm({ ...form, companyId: e.target.value })} style={{ width: '100%' }}>
                <option value="">Select Company</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Role</label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={{ width: '100%' }}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                {editing ? 'New Password (leave blank to keep current)' : 'Password *'}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type={showPassword ? 'text' : 'password'} value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder={editing ? 'Leave blank to keep current' : 'Min 6 characters'}
                  style={{ flex: 1 }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={showPassword} onChange={e => setShowPassword(e.target.checked)} /> Show
                </label>
              </div>
            </div>
            {editing && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 500 }}>Active</label>
                <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />
              </div>
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>App Access</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {APP_OPTIONS.map(a => {
                  const on = hasApp(a.key);
                  return (
                    <button type="button" key={a.key} onClick={() => toggleApp(a.key)} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                      borderRadius: 999, fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
                      border: on ? '1px solid var(--primary, #1a56db)' : '1px solid #d1d5db',
                      background: on ? 'var(--primary, #1a56db)' : '#fff',
                      color: on ? '#fff' : '#374151',
                    }}>
                      {on ? '✓' : '+'} {a.label}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Leave all off to grant access to all apps. ADMIN always has full access.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update User' : 'Create User'}</button>
            <button className="btn-ghost" onClick={() => { setShowForm(false); setEditing(null); setError(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetUserId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div className="card" style={{ width: 360 }}>
            <h3 style={{ fontWeight: 600, marginBottom: 14 }}>Reset Password</h3>
            <input type="password" placeholder="New password" value={resetPassword}
              onChange={e => setResetPassword(e.target.value)} style={{ width: '100%', marginBottom: 12 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={doResetPassword}>Reset</button>
              <button className="btn-ghost" onClick={() => { setResetUserId(null); setResetPassword(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Username</th>
              <th>Company</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td style={{ fontWeight: 600 }}>{u.fullName}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{u.username}</td>
                <td style={{ fontSize: 13 }}>{getCompanyName(u.companyId)}</td>
                <td style={{ color: '#64748b', fontSize: 13 }}>{u.email || '—'}</td>
                <td>
                  <span style={{ ...roleColor(u.role), borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                    {u.role}
                  </span>
                </td>
                <td>
                  <span style={{
                    background: u.active ? '#dcfce7' : '#fee2e2',
                    color: u.active ? '#15803d' : '#b91c1c',
                    borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 600
                  }}>
                    {u.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  {currentUser.role === 'ADMIN' && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openEdit(u)}>Edit</button>
                      <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => { setResetUserId(u.id); setResetPassword(''); }}>🔑 Reset</button>
                      <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => toggleActive(u)}>
                        {u.active ? 'Deactivate' : 'Activate'}
                      </button>
                      {u.username !== currentUser.username && (
                        <button className="btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => deleteUser(u)}>Delete</button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af', padding: 24 }}>No users found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
